import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Product } from '../../../generic/classes/product';
import { ActiveOrderService } from '../../../generic/service/active.order.service';
import { ApplicationService } from '../../../generic/service/application.service';
import { ProductService } from '../../../generic/service/product.service';
import { ShoppingCartService } from '../../../generic/service/shoppingcart.service';
import { TagManagerService } from '../../../generic/service/tag.manager.service';
import { UserService } from '../../../generic/service/user.service';
import { DecimalPipe } from '@angular/common';
import { RenderCheckoutIconComponent } from '../../../generic/component/cart/render.checkout.icon';

@Component({
    selector: 'buy-unit',
    templateUrl: 'buy.unit.component.html',
    styleUrls: ['./buy.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        RenderCheckoutIconComponent,
        DecimalPipe,
    ],
})
export class BuyUnitComponent implements OnInit, OnChanges {
    @Input() public product: Product;
    @Input() itemPrice: number;
    @Input() itemDiscountPrice: number;
    @Input() itemsPerUnit: number;
    @Input() itemsInOrder: number;
    @Input() public unitDescription: string;
    @Input() disabled: boolean;

    public originalPrice: number;
    public unitPrice: number;
    public quantityValue: number;
    public addToCart: UntypedFormGroup;

    public buttonShowPressed: boolean = false;
    public showConfirmation: boolean = false;

    constructor(
        private shoppingCartService: ShoppingCartService,
        private formBuilder: UntypedFormBuilder,
        private userService: UserService,
        private activeOrderService: ActiveOrderService,
        private productService: ProductService,
        private tagManagerService: TagManagerService,
        private changeDetectorRef: ChangeDetectorRef,
        public applicationService: ApplicationService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        this.unitPrice =
            this.product.saleDiscount > 0
                ? this.itemDiscountPrice
                : this.itemPrice;
    }

    isFastCheckout(): boolean {
        return this.userService.isUseFastCheckout();
    }

    ngOnInit() {
        this.addToCart = this.formBuilder.group({
            quantity: ['', Validators.required],
        });

        this.quantityValue = this.getMinimumOrderQuantity();

        this.originalPrice = this.itemPrice * this.itemsPerUnit;

        this.setQuantityValue(this.quantityValue);
        this.addToCartQuantityChanges();

        this.unitPrice =
            this.product.saleDiscount > 0
                ? this.itemDiscountPrice
                : this.itemPrice;
    }

    isEnabled() {
        // If this is a stock product calculate if the available quantity is sufficient to buy another one
        // of this unit.
        if (this.isStockProduct()) {
            return (
                this.disabled ||
                this.getAvailableQuantity() >=
                    this.getMinValue() * this.itemsPerUnit
            );
        }

        return !this.disabled;
    }

    /**
     * @param value
     */
    onSubmit(value: any) {
        this.changeDetectorRef.markForCheck();
        if (this.buttonShowPressed) {
            return;
        }

        const numberOfItemsToOrder: number =
            this.quantityValue * this.itemsPerUnit;

        // Don't show the spinner if the user is first to select an order in the popup.
        if (this.activeOrderService.hasSelectedOrder()) {
            this.buttonShowPressed = true;
        }

        const productOrderDone = this.shoppingCartService.orderProduct(
            this.product,
            numberOfItemsToOrder,
            false,
        );

        // Can be null if the user had first to select an (new) active order.
        if (productOrderDone == null) {
            return;
        }

        productOrderDone.subscribe(
            () => {
                if (this.buttonShowPressed) {
                    this.showConfirmation = true;
                    this.productService.triggerProductAddedToCartEvent(
                        this.product,
                    );
                    this.tagManagerService.trackAddToCart(
                        this.product,
                        numberOfItemsToOrder,
                        numberOfItemsToOrder * this.unitPrice,
                    );
                    this.changeDetectorRef.markForCheck();

                    setTimeout(() => {
                        this.showConfirmation = false;
                        this.buttonShowPressed = false;
                        this.changeDetectorRef.markForCheck();

                        const maximum = this.getMaximumOrderQuantity();
                        if (maximum > 0 && this.quantityValue > maximum) {
                            this.quantityValue = maximum;
                        }
                    }, 2000);
                }
            },
            () => {
                this.buttonShowPressed = false;
                this.changeDetectorRef.markForCheck();
            },
        );
    }

    /**
     * Checks if the combination of quantity and order unit is not less than the minimnum quantity or
     * larger than the available quantity.
     */
    public changeQuantity() {
        const minimum = this.getMinimumOrderQuantity();

        if (this.isStockProduct()) {
            const maximum = this.getMaximumOrderQuantity();

            if (maximum > 0 && this.quantityValue < minimum) {
                this.quantityValue = minimum;
                this.setQuantityValue(this.quantityValue);
            }

            if (maximum > 0 && this.quantityValue > maximum) {
                this.quantityValue = maximum;
                this.setQuantityValue(this.quantityValue);
            }
        }

        // Make sure the value in the input is altered if the minimum is > the current value
        if (minimum > this.quantityValue) {
            this.quantityValue = minimum;
            this.setQuantityValue(this.quantityValue);
        }
    }

    /**
     * returning null will disable the 'max' attribute.
     *
     * @returns {any}
     */
    public getMaxValue(): number {
        if (this.isStockProduct()) {
            return this.getMaximumOrderQuantity();
        }

        return null;
    }

    /**
     * Make sure to take the itemsPerUnit in account - if you need to order two trays minimum, 1 layer should suffice
     * @returns {number}
     */
    public getMinValue(): number {
        return this.getMinimumOrderQuantity();
    }

    public minDisabled(): boolean {
        return this.quantityValue <= this.getMinimumOrderQuantity();
    }

    public plusDisabled(): boolean {
        return (
            this.isStockProduct() &&
            this.quantityValue * this.itemsPerUnit >=
                this.getAvailableQuantity()
        );
    }

    /**
     * Check if quantity + 1 is available
     */
    public plusQuantity() {
        if (
            !this.isStockProduct() ||
            (this.quantityValue + 1) * this.itemsPerUnit <=
                this.getAvailableQuantity()
        ) {
            this.quantityValue = this.quantityValue + 1;
            this.setQuantityValue(this.quantityValue);
        }
    }

    /**
     * Check if quantity -1 is possible
     */
    public minQuantity() {
        if (
            !this.isStockProduct() ||
            (this.quantityValue - 1) * this.itemsPerUnit >=
                this.product.minimumOrderQuantity
        ) {
            this.quantityValue = this.quantityValue - 1;
            this.setQuantityValue(this.quantityValue);
        }
    }

    public isStockProduct(): boolean {
        return this.product.type === this.productService.TYPE_STOCK;
    }

    /**
     * Sets the quantity value of the form input element
     */
    public setQuantityValue(newQuantity: number) {
        this.addToCart.get('quantity')?.setValue(newQuantity);
    }

    /**
     * Listen to changes made to the quantity and update the correct variables
     */
    public addToCartQuantityChanges(): void {
        this.addToCart.valueChanges.subscribe(value => {
            this.quantityValue = value.quantity;
        });
    }

    /**
     * Take the quantity already ordered into account
     *
     * @returns {number}
     */
    private getAvailableQuantity(): number {
        return this.product.availableQuantity - this.itemsInOrder;
    }

    private getMinimumOrderQuantity(): number {
        return this.productService.getMinimumOrderQuantity(
            this.product,
            this.itemsPerUnit,
        );
    }

    private getMaximumOrderQuantity(): number {
        return Math.floor(this.getAvailableQuantity() / this.itemsPerUnit);
    }
}
